/**
 * Labelled routing cases - the thing that was missing for a week.
 *
 * Every prompt edit before this was validated by one manual probe, which is how
 * a fix for over-triggering became under-triggering became a model that marked
 * nothing at all. A change to a `when` line, a pattern, the routing prompt or the
 * decision rules is only an improvement if this set says so.
 *
 * Two layers are scored separately because they fail for different reasons:
 *
 *   PATTERN  - deterministic. Runs in Node, instantly, with no model. A regression
 *              here is a bug in a regex and is always someone's fault.
 *   MODEL    - the LLM fallback, for phrasings no pattern covers. Runs in the
 *              browser via `::template test`. A regression here is a judgement
 *              call and may be the model's ceiling rather than a mistake.
 *
 * `expect` is the id the router should reach by ANY route. `byPattern: true` says
 * the deterministic layer must catch it before the model is consulted - those are
 * the phrasings a product cannot afford to get wrong.
 */

export const ROUTING_CASES = [
  // --- the stock phrases. These must never reach the model. ---
  { text: 'Show me my apps', expect: 'MY_APPS', byPattern: true },
  { text: 'show me my apps', expect: 'MY_APPS', byPattern: true },
  { text: 'my apps', expect: 'MY_APPS', byPattern: true },
  { text: 'list my apps', expect: 'MY_APPS', byPattern: true },
  { text: 'what apps do I have', expect: 'MY_APPS', byPattern: true },
  { text: 'Show me the dashboard', expect: 'DASHBOARD', byPattern: true },
  { text: 'dashboard', expect: 'DASHBOARD', byPattern: true },
  { text: 'show me my account', expect: 'ACCOUNT_SUMMARY', byPattern: true },
  { text: 'my profile', expect: 'ACCOUNT_SUMMARY', byPattern: true },
  { text: 'vcr', expect: 'VCR_TIME_TRAVEL', byPattern: true },
  { text: 'let me time travel debug', expect: 'VCR_TIME_TRAVEL', byPattern: true },

  // --- creating. The action must beat the bare app pattern. ---
  { text: 'create a new app named Scott from the URL https://www.thescottkrause.com',
    expect: 'MY_APPS:CREATE_APP', byPattern: true },
  { text: 'Create a new app named M5Magnet from URL https://www.machfivemarketing.com/',
    expect: 'MY_APPS:CREATE_APP', byPattern: true },
  { text: 'add an app called Acme at https://acme.io', expect: 'MY_APPS:CREATE_APP', byPattern: true },
  { text: 'register a new app', expect: 'MY_APPS:CREATE_APP', byPattern: true },

  // --- the snippet, and its collision with "install" ---
  { text: 'Give me the installation snippet for the app named Scott',
    expect: 'APP_SNIPPET', byPattern: true },
  { text: 'what is the embed code for M5Magnet', expect: 'APP_SNIPPET', byPattern: true },
  { text: 'script tag for Scott', expect: 'APP_SNIPPET', byPattern: true },

  // --- on-domain questions. No pattern; the model has to judge these. ---
  { text: 'what does DRAFT mean', expect: 'EXPLAIN' },
  { text: 'why is my domain not verifying', expect: 'EXPLAIN' },
  { text: 'how does domain verification work', expect: 'EXPLAIN' },

  // --- out of scope. Must decline. ---
  { text: 'what is a cat', expect: 'NONE' },
  { text: 'what is an avocado', expect: 'NONE' },
  { text: 'write me a poem', expect: 'NONE' },
  { text: 'what time is it', expect: 'NONE' },
  { text: 'hello', expect: 'NONE' }
];

export default ROUTING_CASES;
